import { useState, useEffect, useRef } from 'react';
import H3 from '@sdk/components/ui/typography/H3';
import gettext from '@sdk/lang/gettext';
import Text16 from '@sdk/components/ui/typography/Text16';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import styled from 'styled-components';
import TextField from '@sdk/components/forms/TextField';
import PickerField from '@sdk/components/forms/PickerField';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { INPUT_HEIGHT } from '@sdk/components/forms/constants';
import FieldError from '@sdk/components/forms/components/FieldError';
import useSlingApi from '@sdk/api/useSlingApi';
import { SaveClientFlags, UpdateCurrentOrg, UpdateCurrentUser } from '@sdk/authentication/types';
import googleAnalyticsTrack from '@sdk/analytics/google/googleAnalyticsTrack';
import { AnalyticsEvents, GoogleAnalyticsEvents } from '@sdk/analytics/types';
import { SetupOrgPayload } from '@sdk/authentication/api/AuthApi';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import { GENERIC_SERVER_ERROR } from '@sdk/login/constants';
import useAnalytics from '@sdk/analytics/useAnalytics';
import useAuth from '@sdk/authentication/useAuth';

import {
  EMPLOYEES_COUNT_OPTIONS,
  BUSINESS_CATEGORIES_OPTIONS,
  OTHER_BUSINESS_CATEGORY,
  TOAST_BUSINESS_CATEGORIES_OPTIONS,
} from '@web/onboarding/constants';

const MAX_INPUT_LENGTH = 50;

const Container = styled(Column)`
  max-width: 453px;
  flex-grow: 1;
`;

const ButtonContainer = styled(Row)`
  justify-content: flex-end;
`;

const Button = styled(ProgressButton)<{ fullWidth?: boolean }>`
  flex-grow: ${({ fullWidth }): number => (fullWidth ? 1 : 0.5)};
  flex-basis: 0;
`;

type Props = {
  saveClientFlags: SaveClientFlags;
  updateCurrentOrg: UpdateCurrentOrg;
  setFirstRun: () => void;
  nextStep: (category?: string) => void;
  updateCurrentUser: UpdateCurrentUser;
  submitText?: string;
  signupToast?: boolean;
};

export const AccountStep = ({
  updateCurrentOrg,
  saveClientFlags,
  setFirstRun,
  nextStep,
  updateCurrentUser,
  submitText,
  signupToast,
}: Props) => {
  const { currentUser } = useAuth();
  const api = useSlingApi();
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [customIndustry, setCustomIndustry] = useState('');
  const [employeesCount, setEmployeesCount] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const focusFieldRef = useRef(null);
  const { track } = useAnalytics({
    userId: currentUser.id,
    email: currentUser.email,
    orgId: currentUser.org.id,
    toastRestaurantGuids: currentUser.org.toastRestaurantGuids,
  });

  useEffect(() => {
    focusFieldRef.current?.focus();
  }, []);

  useEffect(() => {
    if (currentUser?.org.name && !companyName) {
      setCompanyName(currentUser?.org.name);
    }
  }, [companyName, currentUser?.org?.name]);

  const validate = (): boolean => {
    let valid = true;
    if (
      !companyName.trim() ||
      companyName?.length > MAX_INPUT_LENGTH ||
      !industry ||
      (industry === OTHER_BUSINESS_CATEGORY &&
        (!customIndustry.trim() || customIndustry?.length > MAX_INPUT_LENGTH))
    ) {
      valid = false;
    }

    return valid;
  };

  const submit = async (): Promise<void> => {
    setIsSubmitted(true);
    setServerErrors([]);

    if (!validate()) {
      throw gettext('Form invalid');
    }

    if (!currentUser.consented) {
      try {
        await api.auth.consent();
        updateCurrentUser({ user: { consented: true } });
      } catch (e) {
        if (e?.response.status !== 400) {
          throw e;
        }

        updateCurrentUser({ user: { consented: true } });
      }
    }

    updateCurrentUser({ user: { consented: true } });

    const saveOrg: SetupOrgPayload = {
      name: companyName.trim(),
      businessCategory: industry,
    };

    if (employeesCount) {
      saveOrg.employeesCount = employeesCount;
      saveClientFlags({ flags: { employeesCount } });
    }

    if (customIndustry) {
      saveOrg.customIndustry = customIndustry.trim();
    }

    try {
      const response = await api.auth.setupOrg(currentUser.org.id, saveOrg);

      updateCurrentOrg({ org: response.data });
      googleAnalyticsTrack(GoogleAnalyticsEvents.registrationPart2);
      if (signupToast) {
        track(AnalyticsEvents.signupToast);
      }
      setFirstRun();
      nextStep(industry);
    } catch (e) {
      if (e?.data?.errors) {
        setServerErrors(e.data.errors);
        return;
      }

      if (e?.data?.message) {
        setServerErrors([e.data.message]);
        return;
      }

      setServerErrors([GENERIC_SERVER_ERROR]);
    }
  };

  return (
    <Container>
      <H3 top={20} bottom={10}>
        {gettext('Create a new account')}
      </H3>
      <Text16 themeColor={ThemeColorType.gray}>{gettext('Now, tell us about your company')}</Text16>
      <Column top={15} />
      <Column top={15}>
        <TextField
          maxLength={MAX_INPUT_LENGTH}
          ref={focusFieldRef}
          label={gettext('Company name')}
          placeholder={gettext('Company name')}
          value={companyName}
          onChange={(e): void => setCompanyName(e.currentTarget.value)}
          invalid={isSubmitted && !companyName.trim()}
          error={
            (isSubmitted && !companyName.trim() && gettext('Please provide your company name')) ||
            ''
          }
          height={INPUT_HEIGHT}
        />
      </Column>
      <Column top={15}>
        <PickerField
          label={gettext('Industry')}
          placeholder={gettext('Please select')}
          options={signupToast ? TOAST_BUSINESS_CATEGORIES_OPTIONS : BUSINESS_CATEGORIES_OPTIONS}
          value={industry}
          onChange={(value: string): void => setIndustry(value)}
          invalid={isSubmitted && !industry}
          height={INPUT_HEIGHT}
        />
        <div>
          <FieldError
            invalid={isSubmitted && !industry}
            error={
              (isSubmitted && !industry && gettext('Please provide your company industry')) || ''
            }
          />
        </div>
      </Column>
      {industry === OTHER_BUSINESS_CATEGORY && (
        <Column top={15}>
          <TextField
            maxLength={50}
            label={gettext('Industry name')}
            placeholder={gettext('Industry name')}
            value={customIndustry}
            onChange={(e): void => setCustomIndustry(e.currentTarget.value)}
            invalid={isSubmitted && !customIndustry.trim()}
            error={
              (isSubmitted &&
                !customIndustry.trim() &&
                gettext('Please provide your industry name')) ||
              ''
            }
            height={INPUT_HEIGHT}
          />
        </Column>
      )}
      <Column top={15}>
        <PickerField
          label={gettext('Number of employees')}
          placeholder={gettext('Please select')}
          options={EMPLOYEES_COUNT_OPTIONS}
          value={employeesCount}
          onChange={(value: string): void => setEmployeesCount(value)}
          height={INPUT_HEIGHT}
        />
      </Column>

      {!!serverErrors.length && (
        <Column top={15}>
          {serverErrors.map((error, i) => (
            <FieldError invalid={true} error={error} key={i} />
          ))}
        </Column>
      )}

      <ButtonContainer top={40}>
        <Button fullWidth buttonType={ButtonType.primary} onClick={submit} height={INPUT_HEIGHT}>
          {submitText || gettext('Next')}
        </Button>
      </ButtonContainer>
    </Container>
  );
};
